import React, { useEffect, useRef, useState } from "react";
import { Form, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import Loader from '../widgets/Loader.jsx';
import PropTypes from 'prop-types';
import { useStoreState } from "easy-peasy";
import axios from "../../services/axios.js";
import ChronogramComponent from "./ChronogramComponent.jsx";
import GeoMap from "./GeoMap.jsx";
import ClientCategories from "./ClientCategories.jsx";
import MeanViewsDuration from "./MeanViewsDuration.jsx";
import MeanViewsDurationPercent from "./MeanViewsDurationPercent.jsx";
import CsvDownloadComponent from "../widgets/csvDownload/CsvDownloadComponent.jsx";
import HelperMetiers360 from "../../services/HelpersMetiers360.js";
import { displayDateFilters } from "../activities/VideoPlayGraph.jsx";

const GraphVideosComponent = ({ graphType, videosUuid = [], datesFilter = [undefined, undefined],
    infoDisplayed = "video_views", aggregatable = false, toggleData = null,
    height = "100%", width = "100%" }) => {

    const { apiData: { ACTIONS } } = useStoreState(state => state.actionSlugs);
    const [aggregate, setAggregate] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [graphData, setGraphData] = useState([]);
    const [selectedGraphType, setSelectedGraphType] = useState();
    const [graphTypeToDisplay, setGraphTypeToDisplay] = useState(graphType);
    const [mapTypeToDisplay, setMapTypeToDisplay] = useState("regions");
    const [downloadCSV, setDownloadCSV] = useState(false);

    useEffect(() => {
        toggleData && setSelectedGraphType(toggleData?.find(data => data.defaultChecked)?.value);
    }, [])

    const textOfInfoDisplayed = {
        video_views: "vues",
        unique_clients_total: "clients",
        unique_devices_total: "casques",
    }
    const graphTitleLookup = {
        chronogram: "Évolution dans le temps du nombre de vues hebdomadaires",
        geoMap: `Répartition géographique du nombre de ${textOfInfoDisplayed[infoDisplayed]}`,
        clientCategories: `Nombre de ${textOfInfoDisplayed[infoDisplayed]} par catégories d'organisations`,
        meanViewsDuration: "Durées de lecture par nombre de vues",
        meanViewsDurationPercent: "Durées de lecture par nombre de vues"
    };
    const graphLookup = {
        chronogram: <ChronogramComponent data={graphData} aggregated={aggregate} itemLabel="video" dataValuesLabel="counts_views_week" height={height} />,
        geoMap: <GeoMap data={graphData} infoDisplayed={infoDisplayed} height={height} mapType={mapTypeToDisplay} />,
        clientCategories: <ClientCategories data={graphData} infoDisplayed={infoDisplayed} width={width} height={height} />,
        meanViewsDuration: <MeanViewsDuration data={graphData} aggregated={aggregate} height={height} />,
        meanViewsDurationPercent: <MeanViewsDurationPercent data={graphData} aggregated={aggregate} height={height} />
    };

    const csvSortLookup = {
        chronogram: [
            {
                value: 'video_name',
                label: 'Expérience immersive',
                test: (!aggregatable || !aggregate) && HelperMetiers360.isArrayContainsValue(graphData, "video_name"),
                display: (data) => data.video_name
            },
            {
                value: 'date',
                label: 'Date',
                test: HelperMetiers360.isArrayContainsValue(graphData, "id_week"),
                display: (data) => data.date
            },
            {
                value: 'views',
                label: 'Nombre de vues',
                test: HelperMetiers360.isArrayContainsValue(graphData, "counts_views_week"),
                display: (data) => data.counts_views_week
            }
        ],
        geoMap: [
            {
                value: 'categories',
                label: selectedGraphType === "departments" ? "Département" : "Région",
                test: HelperMetiers360.isArrayContainsValue(graphData, "zone"),
                display: (data) => data.zone
            },
            {
                value: 'views',
                label: `Nombre de ${textOfInfoDisplayed[infoDisplayed]}`,
                test: HelperMetiers360.isArrayContainsValue(graphData, infoDisplayed),
                display: (data) => data[infoDisplayed]
            }
        ],
        clientCategories: [
            {
                value: 'categories',
                label: 'Catégories d\'organisations',
                test: HelperMetiers360.isArrayContainsValue(graphData, "categories"),
                display: (data) => data.categories
            },
            {
                value: 'views',
                label: `Nombre de ${textOfInfoDisplayed[infoDisplayed]}`,
                test: HelperMetiers360.isArrayContainsValue(graphData, infoDisplayed),
                display: (data) => data[infoDisplayed]
            }
        ],
        meanViewsDuration: [
            {
                value: 'video_name',
                label: 'Expérience immersive',
                test: (!aggregatable || !aggregate) && HelperMetiers360.isArrayContainsValue(graphData, "video_name"),
                display: (data) => data.video_name
            },
            {
                value: 'playbac_duration',
                label: 'Durée de lecture (s)',
                test: HelperMetiers360.isArrayContainsValue(graphData, "playbac_duration"),
                display: (data) => data.playbac_duration
            },
            {
                value: 'video_views',
                label: 'Nombre de lecture',
                test: HelperMetiers360.isArrayContainsValue(graphData, "video_views"),
                display: (data) => data.video_views
            }
        ],
        meanViewsDurationPercent: [
            {
                value: 'video_name',
                label: 'Expérience immersive',
                test: (!aggregatable || !aggregate) && HelperMetiers360.isArrayContainsValue(graphData, "video_name"),
                display: (data) => data.video_name
            },
            {
                value: 'playbac_percentage',
                label: 'Durée de lecture (%)',
                test: HelperMetiers360.isArrayContainsValue(graphData, "playbac_percentage")
                    || HelperMetiers360.isArrayContainsValue(graphData, "playback_percentage"),
                display: (data) => data.playbac_percentage
            },
            {
                value: 'views_percentage',
                label: 'Nombre de lecture (%)',
                test: HelperMetiers360.isArrayContainsValue(graphData, "views_percentage"),
                display: (data) => data.views_percentage
            }
        ]
    };
    const csvFilenameLookup = {
        chronogram: "graphe_nombre_vues_hebdomadaires",
        geoMap: "graphe_repartition_geographique",
        clientCategories: "graphe_categories_organisations",
        meanViewsDuration: "graphe_durées_de_lecture",
        meanViewsDurationPercent: "graphe_durées_de_lecture_pourcentage"
    };
    const prevFiltersRef = useRef({
        videosUuid: [],
        datesFilter: [],
        graphTypeToDisplay: null,
        aggregate: null,
    });

    const getCsvTable = () => {
        switch (graphTypeToDisplay) {
            case "chronogram":
                return graphData?.map(data => {
                    return { ...data, date: HelperMetiers360.getdisplayDateType(data.id_week, "day") }
                });
            case "geoMap":
                return graphData?.filter(data => selectedGraphType === "departments"
                    ? !!Number(data.zone) || data.zone === "2A" || data.zone === "2B"
                    : !Number(data.zone) && data.zone != "2A" && data.zone != "2B");
            case "meanViewsDurationPercent":
                return graphData?.map(data => {
                    return {
                        ...data,
                        playbac_percentage: Number(data.playbac_percentage)
                            ? Number(data.playbac_percentage)?.toFixed(2)
                            : Number(data.playback_percentage)?.toFixed(2),
                        views_percentage: Number(data.views_percentage)?.toFixed(2)
                    }
                });
            default:
                return graphData;
        }
    }

    const csvSort = csvSortLookup[graphTypeToDisplay];
    const tableCsv = getCsvTable();
    const csvFilename = csvFilenameLookup[graphTypeToDisplay] + displayDateFilters(datesFilter[0], datesFilter[1]);

    const downloadableBtn = <div className="d-flex justify-content-start">
        {downloadCSV
            ? <CsvDownloadComponent
                contentTable={tableCsv}
                contentSort={csvSort}
                setDownloadCSV={setDownloadCSV}
                filename={csvFilename}
            />
            : <div onClick={() => setDownloadCSV(true)} type="button" role="button">
                <i className="fas fa-download mr-2" /><span className='text-decoration-underline'>Télécharger en csv</span>
            </div>}
    </div>;

    const fetchDataForGraph = () => {
        if (videosUuid.length > 0 && !isFetching) {
            const postUrl = ACTIONS?.producerStats?.getStatsForContents?.url;
            setIsFetching(true);

            axios.post(postUrl, {
                entity: 'videos',
                uuid: videosUuid,
                startDate: datesFilter[0],
                endDate: datesFilter[1],
                statType: graphTypeToDisplay,
                aggregated: aggregate
            }, {
                responseType: "json",
            })
                .then((response) => {
                    setGraphData(response);
                })
                .finally(() => setIsFetching(false));
        }
    }

    useEffect(() => {
        const prev = prevFiltersRef.current;
        const hasChanged =
            JSON.stringify(prev.videosUuid) !== JSON.stringify(videosUuid) ||
            JSON.stringify(prev.datesFilter) !== JSON.stringify(datesFilter) ||
            prev.graphTypeToDisplay !== graphTypeToDisplay ||
            prev.aggregate !== aggregate;

        if (hasChanged && videosUuid.length > 0) {
            fetchDataForGraph();
            prevFiltersRef.current = {
                videosUuid,
                datesFilter,
                graphTypeToDisplay,
                aggregate,
            };
        }
    }, [videosUuid, graphTypeToDisplay, datesFilter, aggregate]);

    useEffect(() => {
        if (aggregatable) {
            fetchDataForGraph();
        }
    }, [aggregate])

    const toggleChange = (newValue) => {
        setSelectedGraphType(newValue);
        const toggleDataSelected = toggleData.find(data => data.value === newValue);
        toggleDataSelected?.graphType && setGraphTypeToDisplay(toggleDataSelected?.graphType);
        toggleDataSelected?.mapType && setMapTypeToDisplay(toggleDataSelected?.mapType);
    }

    return videosUuid?.length > 0 && <>
        <div className="d-flex align-items-center justify-content-between">
            <h4 className={aggregatable ? "mb-1" : "mb-3"}>{graphTitleLookup[graphTypeToDisplay]}</h4>
            {toggleData &&
                <ToggleButtonGroup className='py-2' type="radio" name={"graph" + graphTypeToDisplay} id={"graph" + graphTypeToDisplay} value={selectedGraphType}
                    onChange={toggleChange}>
                    {toggleData.map(data =>
                        <ToggleButton value={data.value} key={data.value} id={data.value} checked={selectedGraphType == data.value} className='mb-0'>
                            {data.label}
                        </ToggleButton>
                    )}
                </ToggleButtonGroup>}
        </div>
        {aggregatable && videosUuid?.length > 1 &&
            <Form.Check id={"aggregate" + graphTypeToDisplay} className="clickable mb-3 ms-3" label="Agréger les données"
                defaultChecked={aggregate} onChange={() => setAggregate((prev) => !prev)} />}
        {isFetching
            ? <Loader />
            : <>
                {downloadableBtn}
                {graphLookup[graphTypeToDisplay]}
            </>
        }
    </>;
}

GraphVideosComponent.propTypes = {
    graphType: PropTypes.string.isRequired,
    videosUuid: PropTypes.array.isRequired,
    infoDisplayed: PropTypes.string,
    height: PropTypes.string,
    width: PropTypes.string,
    datesFilter: PropTypes.array,
    aggregatable: PropTypes.bool,
    toggleData: PropTypes.array,
}

export default GraphVideosComponent;